import React from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
} from '@mui/material'
import { useRoutingStore } from '../stores/routingStore'
import { useDialogRouting } from '../hooks/useDialogRouting'
import { routingUtils } from '../utils/routingUtils'
import { MainMenuSection } from './MainMenu/types/MainMenu.types'

/**
 * Routing Test Component
 * Comprehensive test interface for all routing functionality
 */
const RoutingTest: React.FC = () => {
  const routingStore = useRoutingStore()
  const dialogRouting = useDialogRouting()

  // Test data for all available routes
  const mainRoutes = [
    { section: 'publicLobby', label: 'Dashboard', path: '/dashboard' },
    { section: 'myRooms', label: 'Rooms', path: '/rooms' },
    { section: 'profile', label: 'Profile', path: '/profile' },
    { section: 'settings', label: 'Settings', path: '/settings' },
  ]

  const adminRoutes = [
    { section: 'adminMenu', label: 'Admin Dashboard', path: '/admin' },
    { section: 'adminDashboard', label: 'Admin Dashboard Detail', path: '/admin/dashboard' },
    { section: 'adminEnvironmentConfig', label: 'Environment Config', path: '/admin/environment' },
    { section: 'adminEntityCatalogue', label: 'Entity Catalogue', path: '/admin/entities' },
    { section: 'adminDefaultRooms', label: 'Default Rooms', path: '/admin/rooms' },
    { section: 'adminBillingSettings', label: 'Billing Settings', path: '/admin/billing' },
    { section: 'adminRoomManagement', label: 'Room Management', path: '/admin/room-management' },
    { section: 'adminMapEditorPlugins', label: 'Map Editor Plugins', path: '/admin/map-editor-plugins' },
  ]

  const companyRoutes = [
    { section: 'companyManagement', label: 'Company Management', path: '/company' },
    { section: 'companyOverview', label: 'Company Overview', path: '/company/overview' },
    { section: 'companyDashboard', label: 'Company Dashboard', path: '/company/dashboard' },
    { section: 'companySetup', label: 'Company Setup', path: '/company/setup' },
    { section: 'companyBilling', label: 'Company Billing', path: '/company/billing' },
    { section: 'companyUsers', label: 'Company Users', path: '/company/users' },
    { section: 'companySettings', label: 'Company Settings', path: '/company/settings' },
    { section: 'companyWallet', label: 'Company Wallet', path: '/company/wallet' },
  ]

  const dialogRoutes = [
    { section: 'companyDialog', label: 'Company Dialog', path: '/company/dialogs/company' },
    { section: 'inviteUserDialog', label: 'Invite User Dialog', path: '/company/dialogs/invite-user' },
    { section: 'editUserDialog', label: 'Edit User Dialog', path: '/company/dialogs/edit-user' },
    { section: 'usersDialog', label: 'Users Dialog', path: '/company/dialogs/users' },
    { section: 'companyRoomsDialog', label: 'Company Rooms Dialog', path: '/company/dialogs/company-rooms' },
  ]

  const handleRouteTest = (section: MainMenuSection, path: string) => {
    console.log(`Testing route: ${section} -> ${path}`)
    routingStore.navigateToSection(section)
  }

  const handlePathTest = (path: string) => {
    console.log(`Testing path: ${path}`)
    routingStore.navigateTo(path)
  }

  const handleDialogTest = (dialogName: string) => {
    console.log(`Testing dialog: ${dialogName}`)
    dialogRouting.openDialog(dialogName)
  }

  const RouteCard: React.FC<{
    section: MainMenuSection
    label: string
    path: string
    isActive: boolean
    onTest: () => void
  }> = ({ section, label, path, isActive, onTest }) => (
    <Card sx={{ 
      mb: 2, 
      border: isActive ? '2px solid #389acb' : '1px solid rgba(255, 255, 255, 0.1)',
      backgroundColor: isActive ? 'rgba(56, 154, 203, 0.1)' : 'rgba(0, 0, 0, 0.2)'
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            {label}
          </Typography>
          {isActive && <Chip label="Active" color="primary" size="small" />}
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
          Section: {section}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
          Path: {path}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={onTest}
            sx={{ 
              borderColor: '#389acb', 
              color: '#389acb',
              '&:hover': { backgroundColor: 'rgba(56, 154, 203, 0.1)' }
            }}
          >
            Test Route
          </Button>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => handlePathTest(path)}
            sx={{ 
              borderColor: '#4caf50', 
              color: '#4caf50',
              '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' }
            }}
          >
            Test Path
          </Button>
        </Box>
      </CardContent>
    </Card>
  )

  return (
    <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
      <Typography variant="h4" sx={{ color: 'white', fontWeight: 600, mb: 3 }}>
        Routing System Test
      </Typography>
      
      {/* Debug Information */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 1, color: 'white' }}>
          Debug Information
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontFamily: 'monospace' }}>
          Current URL: {window.location.href}<br/>
          Current Path: {window.location.pathname}<br/>
          Current Section: {routingStore.currentSection}<br/>
          Store Path: {routingStore.currentPath}
        </Typography>
      </Box>

      {/* Current State */}
      <Card sx={{ mb: 4, backgroundColor: 'rgba(56, 154, 203, 0.1)', border: '1px solid #389acb' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            Current State
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Current Section: <strong>{routingStore.currentSection}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Current Path: <strong>{routingStore.currentPath}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Can Go Back: <strong>{routingStore.canGoBack() ? 'Yes' : 'No'}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Can Go Forward: <strong>{routingStore.canGoForward() ? 'Yes' : 'No'}</strong>
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              onClick={routingStore.goBack}
              disabled={!routingStore.canGoBack()}
              sx={{ borderColor: '#389acb', color: '#389acb' }}
            >
              Go Back
            </Button>
            <Button 
              variant="outlined" 
              onClick={routingStore.goForward}
              disabled={!routingStore.canGoForward()}
              sx={{ borderColor: '#389acb', color: '#389acb' }}
            >
              Go Forward
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Main Routes */}
        <Grid item xs={12} md={6}>
          <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>
            Main Routes
          </Typography>
          {mainRoutes.map((route) => (
            <RouteCard
              key={route.section}
              section={route.section as MainMenuSection}
              label={route.label}
              path={route.path}
              isActive={routingStore.currentSection === route.section}
              onTest={() => handleRouteTest(route.section as MainMenuSection, route.path)}
            />
          ))}
        </Grid>

        {/* Admin Routes */}
        <Grid item xs={12} md={6}>
          <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>
            Admin Routes
          </Typography>
          {adminRoutes.map((route) => (
            <RouteCard
              key={route.section}
              section={route.section as MainMenuSection}
              label={route.label}
              path={route.path}
              isActive={routingStore.currentSection === route.section}
              onTest={() => handleRouteTest(route.section as MainMenuSection, route.path)}
            />
          ))}
        </Grid>

        {/* Company Routes */}
        <Grid item xs={12} md={6}>
          <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>
            Company Management Routes
          </Typography>
          {companyRoutes.map((route) => (
            <RouteCard
              key={route.section}
              section={route.section as MainMenuSection}
              label={route.label}
              path={route.path}
              isActive={routingStore.currentSection === route.section}
              onTest={() => handleRouteTest(route.section as MainMenuSection, route.path)}
            />
          ))}
        </Grid>

        {/* Dialog Routes */}
        <Grid item xs={12} md={6}>
          <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>
            Dialog Routes
          </Typography>
          {dialogRoutes.map((route) => (
            <RouteCard
              key={route.section}
              section={route.section as MainMenuSection}
              label={route.label}
              path={route.path}
              isActive={routingStore.currentSection === route.section}
              onTest={() => handleRouteTest(route.section as MainMenuSection, route.path)}
            />
          ))}
        </Grid>
      </Grid>

      <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      {/* Dialog State */}
      <Card sx={{ backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            Dialog State
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(dialogRouting.dialogState).map(([dialogName, isOpen]) => (
              <Grid item xs={6} md={3} key={dialogName}>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: isOpen ? 'rgba(76, 175, 80, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: 1,
                  border: `1px solid ${isOpen ? '#4caf50' : 'rgba(255, 255, 255, 0.1)'}`
                }}>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                    {dialogName}
                  </Typography>
                  <Chip 
                    label={isOpen ? 'Open' : 'Closed'} 
                    color={isOpen ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}

export default RoutingTest
